import React, { memo, useContext, useEffect, useMemo, useState } from "react";

import { CoreLayout, HeaderObserver, isAndroidPlatform } from "@swiggy-private/core-ui";

import CommonHeader from "@cart-client/components/common-header";
import { VerticalScrollTextBanner } from "@swiggy-private/cart-ui";
import { shouldRenderVerticalScrollTextBanner } from "@cart-client/helpers/IncognitoCartHelper";
import { CommonUIWrapperProps } from "@cart-client/interfaces/Cart";

import { CartContext } from "@cart-client/contexts/CartContext";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";
import { COMMON_HEADER_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { CartHeaderContext } from "@cart-client/contexts/CartHeaderContext";
import { isBlackMembershipPlan } from "@cart-client/helpers/CheckBlackMembershipPlan";
import { isCartEmpty } from "@cart-client/helpers/Cart";
import style from "../cart-renderer/CartRenderer.scss";
import {
    getBridgeSdk,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    allowInsetsForAndroid,
} from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/js-utils";
import { CartHeaderLottieContext } from "@cart-client/contexts/CartHeaderLottieContext";
import { LottieCartType } from "@cart-client/hooks/useCommonCartheaderLottie/constants";
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";
import { isCartHeaderTypeLottie } from "@cart-client/helpers/cartHeaderLottie";

const CommonUIWrapper = React.forwardRef<HTMLDivElement, CommonUIWrapperProps>(
    (props: CommonUIWrapperProps, ref): React.ReactElement => {
        const { cartState } = useContext(CartContext);
        const { data } = useContext(CartFlowContext);
        const isGiftingCart = isGiftingCartSource(data?.cartSource);
        const isUserBlackMember = useMemo(
            () => isBlackMembershipPlan(cartState?.reviewedCart),
            [cartState?.reviewedCart],
        );
        const isEmptyCartState = isCartEmpty(cartState?.reviewedCart?.data ?? null);
        const { cartHeaderState } = useContext(CartHeaderContext);
        const { cartHeaderLottieState } = useContext(CartHeaderLottieContext);
        const is99StoreLottie = cartHeaderLottieState?.cartType === LottieCartType._99STORE;
        const isEOCartLottie = cartHeaderLottieState?.cartType === LottieCartType.EO_CART;
        const isCartHeaderTypeLottieExists = isCartHeaderTypeLottie(cartHeaderLottieState?.cartType);
        let headerClassNames = props.headerCSSClass;

        // this is needed to remove scrolled class coming from cartRenderer so that animation can be played on scroll as well
        if (
            cartHeaderState &&
            (cartHeaderState.isExpress ||
                cartHeaderState.isOtg ||
                cartHeaderState.isBolt ||
                isCartHeaderTypeLottieExists) &&
            headerClassNames
        ) {
            headerClassNames = headerClassNames
                .split(" ")
                .filter((className) => className !== style.headerScrolled) // Remove the specific class
                .join(" ");
        }

        // in case of lottie animation we have a dummy text on FE and hence this check
        const isHeaderExtensionExists = cartHeaderState
            ? props.isSavingsBannerExist ||
              cartHeaderState.startAnimation ||
              cartHeaderState.showFinalState ||
              cartHeaderLottieState?.startAnimation ||
              cartHeaderLottieState?.showFinalState
            : props.isSavingsBannerExist;

        useEffect(() => {
            const platform = (GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "").toLowerCase();
            const allowPlatform = !isAndroidPlatform(platform) || allowInsetsForAndroid();
            const shouldChangeStatusBar =
                allowPlatform &&
                !isEmpty(cartHeaderState) &&
                (cartHeaderState.themeChange || cartHeaderState.showFinalState);

            if (shouldChangeStatusBar) {
                getBridgeSdk()
                    .changeStatusBarColor({
                        // android style colors expected to be 6 digit hex
                        style: allowInsetsForAndroid() ? "#ffffff" : "#fff",
                        statusBarMode: "lightContent",
                        bottomBarMode: "default",
                    })
                    .catch(() => void 0);
            }
        }, [cartHeaderState]);

        const isThemeChange = useMemo(
            () => cartHeaderState?.themeChange || cartHeaderLottieState?.themeChange,
            [cartHeaderState, cartHeaderLottieState],
        );

        const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(null);

        const header = (
            <CoreLayout.Header
                className={headerClassNames}
                isUserBlackMember={isUserBlackMember}
                isCart={props.showAddressWidgetTop}
                isPageScrolled={
                    // in case theme change, we want the theme to be present in scrolled state as well
                    !isThemeChange && props.isPageScrolled
                }
                isIncognitoCart={props.isIncognitoCart}
                isHeaderExtensionExists={isHeaderExtensionExists}
                isAddressSelected={props.isAddressSelected}
                isThemeChange={
                    (cartHeaderState && (cartHeaderState.themeChange || cartHeaderState.showFinalState)) ||
                    (cartHeaderLottieState &&
                        (cartHeaderLottieState.themeChange || cartHeaderLottieState.showFinalState))
                }
                isOtg={cartHeaderState?.isOtg ?? false}
                isExpress={cartHeaderState?.isExpress ?? false}
                isBolt={cartHeaderState?.isBolt ?? false}
                is99StoreLottie={is99StoreLottie}
                isEOCartLottie={isEOCartLottie}
                dataTestId={COMMON_HEADER_TEST_IDS.CART_HEADER_PARENT}
                isPopCart={props.isPopCart}
                isGiftingCart={isGiftingCart}
                headerGradient={cartHeaderLottieState?.themeConfig?.header_gradient}
                headerShadow={cartHeaderLottieState?.themeConfig?.header_shadow}
                isJioHotstarFlow={props.isJioHotstarFlow}
            >
                <CommonHeader
                    onBack={props.onBack}
                    containerClass={props.headerCSSClass}
                    isVisible={true}
                    isPageScrolled={props.isPageScrolled}
                    showAddressWidgetTop={props.showAddressWidgetTop}
                    isEmptyCart={isEmptyCartState}
                    useHeaderV2={props.useHeaderV2}
                >
                    {props.header}
                </CommonHeader>
            </CoreLayout.Header>
        );

        return (
            <CoreLayout
                customClassName={props.coreLayoutCustomClassName}
                isJioHotstarFlow={props.isJioHotstarFlow}
                isCartError={props.isCartError}
            >
                <>
                    {shouldRenderVerticalScrollTextBanner(props) && <VerticalScrollTextBanner />}
                    {props.isJioHotstarFlow ? (
                        <HeaderObserver scrollContainer={scrollContainer}>{header}</HeaderObserver>
                    ) : (
                        header
                    )}
                    <CoreLayout.Content
                        className={props.containerCSSClass}
                        ref={ref}
                        contentId={props.containerId}
                        isJioHotstarFlow={props.isJioHotstarFlow}
                        onScrollContainerReady={setScrollContainer}
                    >
                        {props.content}
                    </CoreLayout.Content>
                </>
            </CoreLayout>
        );
    },
);

if (__DEV__) CommonUIWrapper.displayName = "CommonUIWrapper";

export default memo(CommonUIWrapper);
